import React from "react"
import { useContentfulConditionalGate } from "../../lib/contentful/components/ContentfulConditionGate"
import { AdditionalTopicsLink, SubCategoryLink, useReadLearnLinks } from "./shared/ReadLearnLinks"
import { Box, Link, Typography, Tile, IconWithBackground, SurfaceColor, Stack } from "@carrotfertility/carotene-core"
import { Link as RouterLink } from "react-router-dom"
import { Category } from "./shared/CategoryMapper"
import { ContentfulOrderTag, ReadAndLearnTag } from "#/lib/contentful/utils/journeyOrderMapping"
import { getOrderTagId } from "#/lib/contentful/utils/mappingUtils"
import { iconNameToComponent } from "#/services/contentful/mappings/iconNameToComponent"

// we can likely delete this once all category icons are being set with iconName
const contentfulOrderTagToIcon: Record<ReadAndLearnTag, string> = {
  [ContentfulOrderTag.ADOPTION]: "Adoption",
  [ContentfulOrderTag.GESTATIONAL]: "Gestational Surrogacy",
  [ContentfulOrderTag.TRY_PREGNANT]: "Fertility",
  [ContentfulOrderTag.PRESERVATION]: "Preservation",
  [ContentfulOrderTag.ASSISTED_REPRODUCTION]: "Assisted Reproduction",
  [ContentfulOrderTag.PREGNANT]: "Pregnancy",
  [ContentfulOrderTag.PARENTING]: "Parenting",
  [ContentfulOrderTag.MENOPAUSE]: "Menopause",
  [ContentfulOrderTag.LOW_TESTOSTERONE]: "Low Testosterone",
  [ContentfulOrderTag.GENDER_AFFIRMING_CARE]: "Gender Affirming Care"
}

type ReadLearnCategoryProps = {
  category: Category
  index: number
}

const CATEGORY_COLOR: SurfaceColor[] = ["primary", "green", "blue"]

export default function ReadLearnCategory({ category, index }: ReadLearnCategoryProps): JSX.Element {
  const { subCategories } = category
  const { categoryLink } = useReadLearnLinks({ category })

  const subCategoryToConditions = React.useMemo(
    () =>
      subCategories.map((subCategory) => ({
        element: (
          <SubCategoryLink
            key={`id-${subCategory?.fields?.navTitle ?? subCategory?.fields?.title}`}
            {...{ category, subCategory }}
          />
        ),
        conditions: subCategory?.fields?.conditions
      })),
    [category, subCategories]
  )
  const filteredSubCategory = useContentfulConditionalGate(subCategoryToConditions, true)
  const { showAdditionalTopics, additionalTopicCount, linksToShow } = {
    showAdditionalTopics: filteredSubCategory.length > 3,
    additionalTopicCount: filteredSubCategory.length - 3,
    linksToShow: filteredSubCategory.slice(0, 3)
  }

  const color = CATEGORY_COLOR[index % CATEGORY_COLOR.length]
  const tag = getOrderTagId(category) as ReadAndLearnTag
  const iconKey = category.iconName || (tag && contentfulOrderTagToIcon[tag])
  const icon = iconKey && iconNameToComponent[iconKey]

  return (
    <Tile variant="secondary" color={color}>
      <Box
        display="flex"
        flexDirection="column"
        gap={(theme) => theme.spacing(theme.tokens.spacing.xs)}
        minHeight="256px"
      >
        <Link
          component={RouterLink}
          to={categoryLink}
          color="inherit"
          sx={{ textDecoration: "none", containerType: "inline-size" }}
        >
          <Stack
            gap={(theme) => theme.spacing(theme.tokens.spacing.xs)}
            sx={{
              flexDirection: "row",
              alignItems: "center",
              "@container (max-width: 180px)": {
                flexDirection: "column",
                alignItems: "flex-start"
              }
            }}
          >
            {icon ? <IconWithBackground icon={icon} color={color} /> : undefined}
            <Typography variant="h4" component="span">
              {category?.title}
            </Typography>
          </Stack>
        </Link>
        <Box display="flex" flexDirection="column" gap={(theme) => theme.spacing(theme.tokens.spacing.xs)}>
          {linksToShow}
        </Box>
        {showAdditionalTopics ? (
          <AdditionalTopicsLink categoryLink={categoryLink} additionalTopicCount={additionalTopicCount} />
        ) : null}
      </Box>
    </Tile>
  )
}
